import React, { useContext, useState } from 'react';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import { useSupplierTags } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CopyIcon, CheckIcon } from '@phosphor-icons/react';

interface InfoRowProps {
  label: string;
  value?: string | null;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="mb-4">
    <span className="text-xs font-bold text-gray-600 block">{label}</span>
    <span className="text-sm">{value || '-'}</span>
  </div>
);

interface InfoCardProps {
  label: string;
  value?: string | null;
}

const InfoCard: React.FC<InfoCardProps> = ({ label, value }) => (
  <div className="p-4 border rounded-lg">
    <div className="text-xs font-bold text-gray-600 mb-1">{label}</div>
    <div className="text-sm">{value || '-'}</div>
  </div>
);

interface SupplierInfoProps {
  company: CompanyDTO;
  variant?: 'sidebar' | 'tab';
}

export const SupplierInfo: React.FC<SupplierInfoProps> = ({
  company,
  variant = 'tab',
}) => {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);
  const [copied, setCopied] = useState(false);
  const {
    currentTags,
    availableTags,
    currentTagIds,
    isLoading,
    isUpdating,
    handleAddTag,
    handleRemoveTag,
  } = useSupplierTags(company.supplier?.id);

  const handleCopyUuid = () => {
    if (!company.supplier?.id) return;
    navigator.clipboard.writeText(company.supplier.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!company.supplier) {
    return (
      <div className="text-gray-500 text-center py-8">
        No supplier information available
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="p-6">
        <h2 className="text-xl font-medium mb-6">Supplier Information</h2>
        {userIsAdmin && company.supplier?.id && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-gray-600">UUID</span>
              <Badge variant="secondary" className="text-xs">
                Admin
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">{company.supplier.id}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleCopyUuid}
                    >
                      {copied ? (
                        <CheckIcon className="h-3 w-3 text-green-600" />
                      ) : (
                        <CopyIcon className="h-3 w-3" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{copied ? 'Copied!' : 'Copy UUID'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
        {/* <InfoRow label="Contact Name" value={company.supplier.contactName} />
        <InfoRow label="Phone" value={company.supplier.phone} /> */}
        {/* <InfoRow label="Tax ID" value={company.supplier.taxId} />
        <InfoRow label="Payment Terms" value={company.supplier.paymentTerms} /> */}
        {company.supplier?.id && (
          <div className="mb-4">
            <span className="text-xs font-bold text-gray-600 block mb-2">
              Tags
            </span>
            <TagsDisplay
              currentTags={currentTags}
              availableTags={availableTags}
              currentTagIds={currentTagIds}
              isLoading={isLoading}
              isUpdating={isUpdating}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              layout="compact"
            />
          </div>
        )}
      </div>
    );
  }

  // Tab variant (default)
  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Supplier Information</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <InfoCard label="Supplier Name" value={company.supplier.name} />
          <InfoCard label="Status" value={company.supplier.status} />
          {userIsAdmin && company.supplier?.id && (
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-xs font-bold text-gray-600">UUID</div>
                <Badge variant="secondary" className="text-xs">
                  Admin
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm">{company.supplier.id}</div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={handleCopyUuid}
                      >
                        {copied ? (
                          <CheckIcon className="h-3 w-3 text-green-600" />
                        ) : (
                          <CopyIcon className="h-3 w-3" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{copied ? 'Copied!' : 'Copy UUID'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
          <InfoCard label="Contact Name" value={company.supplier.contactName} />
          <InfoCard label="Email" value={company.supplier.email} />
          <InfoCard label="Phone" value={company.supplier.phone} />
          <InfoCard label="Tax ID" value={company.supplier.taxId} />
        </div>
        {company.supplier.paymentTerms && (
          <InfoCard
            label="Payment Terms"
            value={company.supplier.paymentTerms}
          />
        )}
        {company.supplier?.id && (
          <div className="p-4 border rounded-lg">
            <div className="text-xs font-bold text-gray-600 mb-2">Tags</div>
            <TagsDisplay
              currentTags={currentTags}
              availableTags={availableTags}
              currentTagIds={currentTagIds}
              isLoading={isLoading}
              isUpdating={isUpdating}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              layout="compact"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierInfo;
